import i18n from '@i18next/i18n';
import { ICTBlip } from '@tencent/slide-sdk-model';
import { DeepPartial, DeepReadonly } from 'ts-essentials';

export enum EDataType {
    string = 1,
    number = 2
}

// 图片相关
export const ImagePropsField = {
    // 图片校正相关
    ClARITY: {
        name: 'sharpenSoften', getSdkValue: getSharpenSoften, getSdkPath: getSharpenSoftenPath, title: i18n.t('清晰度'), defaultValue: 0, coefficient: 1000, dataType: EDataType.number,
    },
    BRIGHTNESS: {
        name: 'brightness', getSdkValue: getBrightness, getSdkPath: getBrightnessPath, title: i18n.t('亮度'), defaultValue: 0, coefficient: 1000, dataType: EDataType.number,
    },
    CONTRAST: {
        name: 'contrast', getSdkValue: getContrast, getSdkPath: getContrastPath, title: i18n.t('对比度'), defaultValue: 0, coefficient: 1000, dataType: EDataType.number,
    },

    // 图片颜色相关
    SATURATE: {
        name: 'saturate', getSdkValue: getSaturation, getSdkPath: getSaturationPath, title: i18n.t('饱和度'), defaultValue: 100, coefficient: 1000, dataType: EDataType.string,
    },
    COLORTEMPERATURE: {
        name: 'colorTemperature', getSdkValue: getColorTemperature, getSdkPath: getColorTemperaturePath, title: i18n.t('色温'), defaultValue: 6500, coefficient: 1, dataType: EDataType.number,
    },
};

// 从 sdkData 里获取'清晰度'值
function getSharpenSoften(sdkData: DeepReadonly<DeepPartial<ICTBlip>>): number | undefined {
    return imageEffectList(sdkData)?.find(imgEffect => !!imgEffect?.sharpenSoften)?.sharpenSoften?.amount;
}
// 从 sdkData 里获取'清晰度'路径
function getSharpenSoftenPath(sdkData: DeepReadonly<DeepPartial<ICTBlip>>): string {
    let index = imageEffectList(sdkData)?.findIndex(imgEffect => !!imgEffect?.sharpenSoften) ?? 0;
    index = index < 0 ? 0 : index;
    return `extLst.ext[0].imgProps[0].imgLayer.imgEffect[${index}].sharpenSoften.amount`;
}

// 从 sdkData 里获取'亮度'值
function getBrightness(sdkData: DeepReadonly<DeepPartial<ICTBlip>>): number | undefined {
    return imageEffectBrightnessContrast(sdkData)?.bright;
}
// 从 sdkData 里获取'亮度'路径
function getBrightnessPath(sdkData: DeepReadonly<DeepPartial<ICTBlip>>): string {
    let index = imageEffectList((sdkData))?.findIndex(imgEffect => !!imgEffect?.brightnessContrast) ?? 0;
    index = index < 0 ? 0 : index;
    return `extLst.ext[0].imgProps[0].imgLayer.imgEffect[${index}].brightnessContrast.bright`;
}

// 从 sdkData 里获取'对比度'值
function getContrast(sdkData: DeepReadonly<DeepPartial<ICTBlip>>): number | undefined {
    return imageEffectBrightnessContrast(sdkData)?.contrast;
}
// 从 sdkData 里获取'对比度'路径
function getContrastPath(sdkData: DeepReadonly<DeepPartial<ICTBlip>>): string {
    let index = imageEffectList((sdkData))?.findIndex(imgEffect => !!imgEffect?.brightnessContrast) ?? 0;
    index = index < 0 ? 0 : index;
    return `extLst.ext[0].imgProps[0].imgLayer.imgEffect[${index}].brightnessContrast.contrast`;
}

// 从 sdkData 里获取'饱和度'值
function getSaturation(sdkData: DeepReadonly<DeepPartial<ICTBlip>>): number | undefined {
    const satStr = imageEffectList((sdkData))?.find(imgEffect => !!imgEffect?.saturation)?.saturation?.sat;
    if (satStr === undefined || satStr === null) {
        return undefined;
    }
    if (Number(satStr) || Number(satStr) === 0) {
        return Number(satStr);
    }
}
// 从 sdkData 里获取'饱和度'路径
function getSaturationPath(sdkData: DeepReadonly<DeepPartial<ICTBlip>>): string {
    let index = imageEffectList((sdkData))?.findIndex(imgEffect => !!imgEffect?.saturation) ?? 0;
    index = index < 0 ? 0 : index;
    return `extLst.ext[0].imgProps[0].imgLayer.imgEffect[${index}].saturation.sat`;
}

// 从 sdkData 里获取'色温'值
function getColorTemperature(sdkData: DeepReadonly<DeepPartial<ICTBlip>>): number | undefined {
    return imageEffectList((sdkData))?.find(imgEffect => !!imgEffect?.colorTemperature)?.colorTemperature?.colorTemp;
}
// 从 sdkData 里获取'色温'路径
function getColorTemperaturePath(sdkData: DeepReadonly<DeepPartial<ICTBlip>>): string {
    let index = imageEffectList((sdkData))?.findIndex(imgEffect => !!imgEffect?.colorTemperature) ?? 0;
    index = index < 0 ? 0 : index;
    return `extLst.ext[0].imgProps[0].imgLayer.imgEffect[${index}].colorTemperature.colorTemp`;
}

function imageEffectList(sdkData: DeepReadonly<DeepPartial<ICTBlip>>) {
    return sdkData?.extLst?.ext?.[0]?.imgProps?.[0]?.imgLayer?.imgEffect;
}

function imageEffectBrightnessContrast(sdkData: DeepReadonly<DeepPartial<ICTBlip>>) {
    return imageEffectList(sdkData)?.find(imgEffect => !!imgEffect?.brightnessContrast)?.brightnessContrast;
}

export const DEFAULT_REGULATE = {
    [ImagePropsField.ClARITY.name]: ImagePropsField.ClARITY.defaultValue,
    [ImagePropsField.BRIGHTNESS.name]: ImagePropsField.BRIGHTNESS.defaultValue,
    [ImagePropsField.CONTRAST.name]: ImagePropsField.CONTRAST.defaultValue,
};

export const DEFAULT_IMAGE_COLOR = {
    [ImagePropsField.SATURATE.name]: ImagePropsField.SATURATE.defaultValue,
    [ImagePropsField.COLORTEMPERATURE.name]: ImagePropsField.COLORTEMPERATURE.defaultValue,
};
